import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { services } from '../mock';
import { Button } from '../components/ui/button';
import { Reveal } from '../components/motion/Reveal';
import { Seo } from '../components/Seo';
import { seoPages } from '../seo';

const ServiceCard = ({ service, index }) => {
  const { t } = useLanguage();
  const Icon = Icons[service.icon];
  const lang = t({ sl: 'sl', en: 'en', it: 'it' });

  return (
    <Link
      to={`/services/${service.slug}`}
      className="group relative flex flex-col h-full rounded-2xl bg-[#111114] border border-white/10 overflow-hidden transition-[border-color,box-shadow,transform] duration-300 hover:border-white/25 hover:shadow-[0_20px_50px_rgba(0,0,0,0.6)] md:hover:-translate-y-1"
      data-testid={`service-card-${service.slug}`}
    >
      <span className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />

      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={service.heroImage || service.image}
          alt={t(service.title)}
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111114] via-[#111114]/30 to-transparent" />
        <span className="absolute top-4 left-4 text-[11px] font-mono tracking-[0.25em] text-white/70 bg-black/50 backdrop-blur-sm px-2.5 py-1 rounded-full">
          {String(index + 1).padStart(2, '0')}
        </span>
      </div>

      <div className="relative flex flex-col flex-1 px-5 sm:px-6 pt-10 pb-6">
        <div className="absolute left-5 sm:left-6 -top-7 w-14 h-14 rounded-2xl bg-black border border-white/15 flex items-center justify-center shadow-xl group-hover:border-cyan-400/60 transition-colors">
          <Icon className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight mb-2 uppercase" style={{ fontFamily: "'Exo 2', sans-serif" }}>
          {t(service.title)}
        </h2>
        <p className="text-sm sm:text-base text-[#b0b0b0] leading-relaxed mb-5">
          {t(service.description)}
        </p>

        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
          {service.features[lang].slice(0, 4).map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-xs sm:text-[13px] text-[#d4d4d8] leading-snug">
              <span className="mt-[2px] w-4 h-4 rounded-full bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                <Check className="w-2.5 h-2.5 text-black" strokeWidth={3} />
              </span>
              {feature}
            </li>
          ))}
        </ul>

        <div className="mt-auto flex items-center justify-between pt-4 border-t border-white/10">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60 group-hover:text-white transition-colors">
            {t({ sl: 'Več informacij', en: 'Learn more', it: 'Maggiori informazioni' })}
          </span>
          <span className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white group-hover:bg-white group-hover:text-black group-hover:border-white transition-colors">
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
};

const Services = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-[#000000] pt-28 md:pt-32 pb-24">
      <Seo title={seoPages.services.title} description={seoPages.services.description} />
      <div className="container mx-auto px-5 md:px-10">
        <Reveal className="max-w-3xl mb-12 md:mb-16">
          <span className="inline-block text-[11px] font-mono uppercase tracking-[0.3em] text-white/60 border border-white/15 rounded-full px-3 py-1 mb-5">
            {t({ sl: '8 storitev · Koper', en: '8 services · Koper', it: '8 servizi · Capodistria' })}
          </span>
          <h1 className="heading-2 text-[white] mb-4">
            {t({ sl: 'NAŠE STORITVE', en: 'OUR SERVICES', it: 'I NOSTRI SERVIZI' })}
          </h1>
          <p className="body-large text-[#b0b0b0]">
            {t({
              sl: 'Od vizitk do velikoformatnih tiskov - ponujamo celovite tiskarske storitve za podjetja in posameznike.',
              en: 'From business cards to large format prints - we offer comprehensive printing services for businesses and individuals.',
              it: 'Dai biglietti da visita alle stampe grande formato - offriamo servizi di stampa completi per aziende e privati.'
            })}
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8" data-testid="services-grid">
          {services.map((service, idx) => (
            <Reveal key={service.id} delay={(idx % 3) * 0.1} y={36} className="h-full">
              <ServiceCard service={service} index={idx} />
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 md:mt-20 rounded-2xl border border-white/10 bg-[#111114] p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="services-cta">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white uppercase mb-2" style={{ fontFamily: "'Exo 2', sans-serif" }}>
              {t({ sl: 'Ne najdete, kar iščete?', en: "Can't find what you're looking for?", it: 'Non trovate quello che cercate?' })}
            </h2>
            <p className="text-[#b0b0b0] text-sm md:text-base">
              {t({
                sl: 'Pošljite nam povpraševanje – skupaj najdemo najboljšo rešitev za vaš projekt.',
                en: 'Send us an inquiry – together we will find the best solution for your project.',
                it: 'Inviateci una richiesta – insieme troveremo la soluzione migliore per il vostro progetto.'
              })}
            </p>
          </div>
          <Link to="/contact" className="md:flex-shrink-0">
            <Button className="btn-primary min-w-[220px]" data-testid="services-contact-btn">
              {t({ sl: 'Kontaktirajte nas', en: 'Contact Us', it: 'Contattaci' })}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </Reveal>
      </div>
    </div>
  );
};

export default Services;
